/**
 * <s-welcome-popup> — opens the first-order email popup once per visitor.
 *
 * Opens the inner <dialog> after `delay` seconds (default 8) unless the visitor
 * has already seen or dismissed it (localStorage key below). The server opens it
 * directly after a submit (via <s-dialog open-on-connect>), so this only handles
 * the first, unprompted show.
 *
 * Attributes:
 *   delay — seconds before the popup opens (default 8)
 */
const STORAGE_KEY = 'bcb-welcome-popup-seen';

class SWelcomePopup extends HTMLElement {
  #timer = null;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    // Remember the visitor saw it whenever it closes (dismissed or after signing up).
    dialog.addEventListener('close', () => this.#markSeen());

    if (dialog.open || this.#hasSeen()) return;

    const delay = Number(this.getAttribute('delay')) || 8;
    this.#timer = window.setTimeout(() => {
      if (!dialog.open && !document.querySelector('dialog[open]')) {
        dialog.showModal();
        this.#markSeen();
      }
    }, delay * 1000);
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
  }

  #hasSeen() {
    try {
      return window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  #markSeen() {
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // Storage blocked (private mode): the popup may show again next visit.
    }
  }
}

if (!customElements.get('s-welcome-popup')) {
  customElements.define('s-welcome-popup', SWelcomePopup);
}
